import React, { useState, useEffect } from 'react';
import './App.css';

const BREATHING_TECHNIQUES = {
    basic: {
        name: 'Basic',
        breatheIn: 5,
        hold: 3,
        breatheOut: 6,
        cycles: 6
    },
    intermediate: {
        name: 'Intermediate',
        breatheIn: 8,
        hold: 5,
        breatheOut: 8,
        cycles: 4
    }
};

const PHASES = {
    BREATHE_IN: 'breathe-in',
    HOLD: 'hold',
    BREATHE_OUT: 'breathe-out',
    COMPLETE: 'complete'
};

function App() {
    const [selectedTechnique, setSelectedTechnique] = useState('basic');
    const [isActive, setIsActive] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(PHASES.BREATHE_IN);
    const [timeLeft, setTimeLeft] = useState(0);
    const [currentCycle, setCurrentCycle] = useState(1);
    const [progress, setProgress] = useState(0);

    const technique = BREATHING_TECHNIQUES[selectedTechnique];

    useEffect(() => {
        let interval = null;

        if (isActive && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(time => {
                    if (time <= 1) {
                        // Move to next phase
                        moveToNextPhase();
                        return 0;
                    }
                    return time - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isActive, timeLeft]);

    const moveToNextPhase = () => {
        if (currentPhase === PHASES.BREATHE_IN) {
            setCurrentPhase(PHASES.HOLD);
            setTimeLeft(technique.hold);
        } else if (currentPhase === PHASES.HOLD) {
            setCurrentPhase(PHASES.BREATHE_OUT);
            setTimeLeft(technique.breatheOut);
        } else if (currentPhase === PHASES.BREATHE_OUT) {
            if (currentCycle < technique.cycles) {
                setCurrentCycle(prev => prev + 1);
                setCurrentPhase(PHASES.BREATHE_IN);
                setTimeLeft(technique.breatheIn);
            } else {
                setCurrentPhase(PHASES.COMPLETE);
                setIsActive(false);
                setTimeLeft(0);
            }
        }
    };

    const startBreathing = () => {
        setIsActive(true);
        setCurrentPhase(PHASES.BREATHE_IN);
        setTimeLeft(technique.breatheIn);
        setCurrentCycle(1);
        setProgress(0);
    };

    const stopBreathing = () => {
        setIsActive(false);
        setCurrentPhase(PHASES.BREATHE_IN);
        setTimeLeft(0);
        setCurrentCycle(1);
        setProgress(0);
    };

    const getPhaseText = () => {
        switch (currentPhase) {
            case PHASES.BREATHE_IN:
                return 'Breathe In';
            case PHASES.HOLD:
                return 'Hold';
            case PHASES.BREATHE_OUT:
                return 'Breathe Out';
            case PHASES.COMPLETE:
                return 'Complete!';
            default:
                return 'Ready to Start';
        }
    };

    const getCircleClass = () => {
        if (!isActive && currentPhase !== PHASES.COMPLETE) return 'breathing-circle';

        switch (currentPhase) {
            case PHASES.BREATHE_IN:
                return 'breathing-circle breathe-in';
            case PHASES.HOLD:
                return 'breathing-circle hold';
            case PHASES.BREATHE_OUT:
                return 'breathing-circle breathe-out';
            case PHASES.COMPLETE:
                return 'breathing-circle complete';
            default:
                return 'breathing-circle';
        }
    };

    // Calculate overall progress
    useEffect(() => {
        if (isActive) {
            const totalDuration = technique.cycles * (technique.breatheIn + technique.hold + technique.breatheOut);
            const completedCycles = currentCycle - 1;
            const completedDuration = completedCycles * (technique.breatheIn + technique.hold + technique.breatheOut);

            let currentCycleDuration = 0;
            if (currentPhase === PHASES.BREATHE_IN) {
                currentCycleDuration = technique.breatheIn - timeLeft;
            } else if (currentPhase === PHASES.HOLD) {
                currentCycleDuration = technique.breatheIn + (technique.hold - timeLeft);
            } else if (currentPhase === PHASES.BREATHE_OUT) {
                currentCycleDuration = technique.breatheIn + technique.hold + (technique.breatheOut - timeLeft);
            }

            const totalCompleted = completedDuration + currentCycleDuration;
            setProgress((totalCompleted / totalDuration) * 100);
        }
    }, [currentPhase, timeLeft, currentCycle, technique, isActive]);

    return (
        <div className="App">
            <header className="app-header">
                <div className="header-brand">
                    <div className="brand-name">WhiteApps NZ</div>
                    <div className="license-info">© Copyright 2025</div>
                </div>
                <h1>Breathe Now</h1>
                <p>Breathe your way to calm</p>
            </header>

            <main className="app-main">
                <div className="technique-selector">
                    <h3>Choose Breathing Technique</h3>
                    <div className="technique-buttons">
                        {Object.entries(BREATHING_TECHNIQUES).map(([key, tech]) => (
                            <button
                                key={key}
                                className={`technique-btn ${selectedTechnique === key ? 'active' : ''}`}
                                onClick={() => setSelectedTechnique(key)}
                                disabled={isActive}
                            >
                                <div className="technique-name">{tech.name}</div>
                                <div className="technique-pattern">
                                    {tech.breatheIn}s - {tech.hold}s - {tech.breatheOut}s
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="breathing-container">
                    <div className="breathing-info">
                        <div className="phase-text">{getPhaseText()}</div>
                        {isActive && (
                            <div className="timer">{timeLeft}</div>
                        )}
                        {isActive && (
                            <div className="cycle-counter">
                                Cycle {currentCycle} of {technique.cycles}
                            </div>
                        )}
                    </div>

                    <div className="circle-container">
                        <div
                            key={`${currentPhase}-${currentCycle}`}
                            className={getCircleClass()}
                            style={{
                                '--breathe-in-duration': `${technique.breatheIn}s`,
                                '--hold-duration': `${technique.hold}s`,
                                '--breathe-out-duration': `${technique.breatheOut}s`,
                                '--dot-radius': '100px'
                            }}
                        >
                            <div className="circle-dot"></div>
                        </div>
                    </div>

                    <div className="progress-container">
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="controls">
                        {!isActive ? (
                            <button
                                className="control-btn start-btn"
                                onClick={startBreathing}
                                disabled={currentPhase === PHASES.COMPLETE}
                            >
                                {currentPhase === PHASES.COMPLETE ? 'Session Complete' : 'Start Breathing'}
                            </button>
                        ) : (
                            <button
                                className="control-btn stop-btn"
                                onClick={stopBreathing}
                            >
                                Stop
                            </button>
                        )}
                        {currentPhase === PHASES.COMPLETE && (
                            <button
                                className="control-btn restart-btn"
                                onClick={() => {
                                    setCurrentPhase(PHASES.BREATHE_IN);
                                    setProgress(0);
                                }}
                            >
                                Start New Session
                            </button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

export default App;